// services/BillService.js

import axiosInstance from './axiosInstance';
import { formatDate } from '../utils/dateUtils';

// Debug utility functions - only log in development environment
const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

const debugError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(...args);
  }
};

const debugWarn = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(...args);
  }
};

// Function to add a new bill
export async function addBill(newBill) {
  try {
    // Input validation with improved error handling
    if (!newBill || typeof newBill !== 'object') {
      throw new Error('Invalid bill data');
    }
    
    // Basic validation
    if (typeof newBill.name !== 'string' || !newBill.name.trim()) {
      throw new Error('Invalid bill name');
    }
    
    // Better amount validation that handles string values
    if (newBill.amount !== 0 && (!newBill.amount || isNaN(parseFloat(newBill.amount)))) {
      throw new Error('Invalid amount');
    }
    
    // Date validation
    if (!newBill.due_date || isNaN(new Date(newBill.due_date).getTime())) {
      throw new Error('Invalid due date');
    }
    
    // Bank account validation
    if (!newBill.bank_account || isNaN(parseInt(newBill.bank_account))) {
      throw new Error('Invalid bank account');
    }
    
    // Ensure numBills is a proper integer
    if (newBill.num_bills) {
      if (isNaN(parseInt(newBill.num_bills))) {
        throw new Error('Invalid number of bills');
      }
      // Make sure num_bills is an integer
      newBill.num_bills = parseInt(newBill.num_bills);
    }
    
    // Create a copy of the data to ensure proper types
    const billData = {
      ...newBill,
      amount: parseFloat(newBill.amount).toFixed(2),
      bank_account: parseInt(newBill.bank_account),
      num_bills: parseInt(newBill.num_bills || 1)
    };
    
    
    const response = await axiosInstance.post('/bills/', billData);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (!response.data.id || typeof response.data.name !== 'string') {
      throw new Error('Invalid bill response structure');
    }

    return response.data;
  } catch (error) {
    debugError('Error adding bill:', error.message);
    debugError('Error adding bill (full details):', error);
    
    // Enhanced error reporting
    if (error.response && error.response.data) {
      debugError('API error details:', error.response.status);
      
      // If there's a specific error message from the API, use it
      if (error.response) {
        // Log the error safely for debugging (without sensitive data)
        debugError('Bill service error status:', error.response.status);
        
        // Return a generic message to the user
        throw new Error('Unable to process bill operation. Please try again later.');
      }
      
      // If there are field-specific errors, format them nicely
      if (typeof error.response.data === 'object') {
        const errorMessages = Object.entries(error.response.data)
          .map(([field, messages]) => {
            if (Array.isArray(messages)) {
              return `${field}: ${messages.join(', ')}`;
            }
            return `${field}: ${messages}`;
          })
          .join('; ');
          
        if (errorMessages) {
          throw new Error(errorMessages);
        }
      }
    }
    
    throw error;
  }
}

// Function to update bill details
export async function updateBillDetails(billId, updatedBill, billSeriesId) {
  // Input validation
  if (!billId || typeof billId !== 'number') {
    throw new Error('Invalid bill ID');
  }
  if (!updatedBill || typeof updatedBill !== 'object') {
    throw new Error('Invalid bill data');
  }
  
  // Only validate billSeriesId if it was provided - it's a UUID string
  if (billSeriesId !== null && billSeriesId !== undefined) {
    if (typeof billSeriesId !== 'string' || billSeriesId.trim() === '') {
      debugError('Invalid bill_series_id');
      throw new Error('Invalid bill series ID');
    }
  }
  
  if (updatedBill.bank_account && typeof updatedBill.bank_account !== 'number') {
    throw new Error('Invalid bank account');
  }

  try {
    // Include the billSeriesId in the updatedBill object if provided and valid
    const billData = { ...updatedBill };
    // Note: description and notes are already sanitized by sanitizeMultilineText in the component
    
    if (billSeriesId && typeof billSeriesId === 'string' && billSeriesId.trim() !== '') {
      billData.billSeriesId = billSeriesId;
    }

    const response = await axiosInstance.put(`/bills/${encodeURIComponent(billId)}/`, billData);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }

    return response.data;
  } catch (error) {
    debugError('Error updating bill details:', error.message);
    debugError('Error updating bill details (full details):', error);
    if (error.response) {
      debugError('API error - Status:', error.response.status);
      // For user-facing errors, use generic messages
      if (error.response.status >= 400 && error.response.status < 500) {
        throw new Error('Invalid bill data. Please check your inputs.');
      } else {
        throw new Error('Server error updating bill. Please try again.');
      }
    }
    throw new Error('Network error updating bill. Please check your connection.');
  }
}

// Function to get bills due or paid in the current fortnight, and prior to current week if unpaid
export const getFortnightBills = async (currentDate) => {
  // Input validation
  if (currentDate && isNaN(new Date(currentDate).getTime())) {
    debugError('Invalid date format for getFortnightBills');
    return { current_week_bills: [], next_week_bills: [] };
  }

  try {
    const response = await axiosInstance.get('/fortnight/', {
      params: {
        current_date: currentDate ? encodeURIComponent(currentDate) : null
      }
    });

    // Response validation with fallbacks for safety
    if (!response.data || typeof response.data !== 'object') {
      debugWarn('Invalid response data structure from fortnight endpoint');
      return { current_week_bills: [], next_week_bills: [] };
    }
    
    // Ensure we have current_week_bills and next_week_bills arrays
    const current_week_bills = Array.isArray(response.data.current_week_bills) 
      ? response.data.current_week_bills 
      : [];
      
    const next_week_bills = Array.isArray(response.data.next_week_bills) 
      ? response.data.next_week_bills 
      : [];

    // Filter out invalid items
    const valid_current_week_bills = current_week_bills.filter(bill => 
      bill && typeof bill === 'object' && bill.id && bill.name
    );
    
    const valid_next_week_bills = next_week_bills.filter(bill => 
      bill && typeof bill === 'object' && bill.id && bill.name
    );

    return { 
      current_week_bills: valid_current_week_bills, 
      next_week_bills: valid_next_week_bills 
    };
  } catch (error) {
    debugError('Error fetching fortnight bills:', error.message);
    debugError('Error fetching fortnight bills (full details):', error);
    // Return empty arrays to avoid UI crashes
    return { current_week_bills: [], next_week_bills: [] };
  }
};


// Function to get bills with optional filters and sorting
export async function getBills(filterDate, isPaid, sortField, sortOrder, bankAccountId, page = 1, limit = 50) {
  let url = '/bills/';

  const params = new URLSearchParams();
  
  if (filterDate) {
    params.append('due_date__gte', filterDate);
  }

  // Only add the paid parameter if it's explicitly true or false
  // This ensures we don't filter by paid status when isPaid is null or undefined
  if (isPaid === true) {
    params.append('paid', 'true');
  } else if (isPaid === false) {
    params.append('paid', 'false');
  }

  if (bankAccountId) {
    params.append('bank_account', bankAccountId);
  }

  if (sortField && sortOrder) {
    params.append('ordering', `${sortOrder === 'asc' ? '' : '-'}${sortField}`);
  }
  
  // Add pagination parameters
  params.append('page', page);
  params.append('limit', limit);

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  try {
    const response = await axiosInstance.get(url);

    // Check if response data is null or not an array (could be an error object)
    if (!response.data) {
      debugWarn('Empty response data from bills endpoint');
      return { bills: [], total: 0, hasMore: false };
    }
    
    // Handle paginated response format
    if (response.data.results && Array.isArray(response.data.results)) {
      const validBills = response.data.results.filter(bill => 
        bill && typeof bill === 'object' && bill.id && bill.name
      );
      
      return {
        bills: validBills,
        total: response.data.count || validBills.length,
        hasMore: !!response.data.next,
        currentPage: page
      };
    }
    
    // Handle non-paginated response (backward compatibility)
    if (Array.isArray(response.data)) {
      const validBills = response.data.filter(bill => 
        bill && typeof bill === 'object' && bill.id && bill.name
      );
      
      return {
        bills: validBills,
        total: validBills.length,
        hasMore: false,
        currentPage: 1
      };
    }

    debugWarn('Unexpected response format from bills endpoint');
    return { bills: [], total: 0, hasMore: false };
  } catch (error) {
    debugError('Error fetching bills:', error.message);
    debugError('Error fetching bills (full details):', error);
    // Return empty result object instead of throwing error to avoid UI crashes
    return { bills: [], total: 0, hasMore: false };
  }
}

// Search bills with full filter support (used by Bill Search tab)
export async function searchBills({
  searchTerm = '',
  dueStartDate = '',
  dueEndDate = '',
  paidStartDate = '',
  paidEndDate = '',
  isPaid = null,
  sortField = 'due_date',
  sortOrder = 'asc',
  bankAccountId = null,
  page = 1,
  limit = 50,
} = {}) {
  let url = '/bills/';
  const params = new URLSearchParams();

  const term = (searchTerm || '').trim();
  if (term) params.append('search', term);
  if (dueStartDate) params.append('due_date__gte', dueStartDate);
  if (dueEndDate) params.append('due_date__lte', dueEndDate);
  if (paidStartDate) params.append('paid_date__gte', paidStartDate);
  if (paidEndDate) params.append('paid_date__lte', paidEndDate);
  if (isPaid === true) params.append('paid', 'true');
  else if (isPaid === false) params.append('paid', 'false');
  if (bankAccountId) params.append('bank_account', bankAccountId);
  if (sortField && sortOrder) {
    params.append('ordering', `${sortOrder === 'asc' ? '' : '-'}${sortField}`);
  }
  params.append('page', page);
  params.append('limit', limit);

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  try {
    const response = await axiosInstance.get(url);

    if (!response.data) {
      debugWarn('Empty response data from bills search');
      return { bills: [], total: 0, hasMore: false, currentPage: page };
    }

    if (response.data.results && Array.isArray(response.data.results)) {
      const validBills = response.data.results.filter(
        (bill) => bill && typeof bill === 'object' && bill.id && bill.name
      );
      return {
        bills: validBills,
        total: response.data.count || validBills.length,
        hasMore: !!response.data.next,
        currentPage: page,
      };
    }

    if (Array.isArray(response.data)) {
      const validBills = response.data.filter(
        (bill) => bill && typeof bill === 'object' && bill.id && bill.name
      );
      return {
        bills: validBills,
        total: validBills.length,
        hasMore: false,
        currentPage: 1,
      };
    }

    debugWarn('Unexpected response format from bills search');
    return { bills: [], total: 0, hasMore: false, currentPage: page };
  } catch (error) {
    debugError('Error searching bills:', error.message);
    debugError('Error searching bills (full details):', error);
    return { bills: [], total: 0, hasMore: false, currentPage: page };
  }
}

// Fetch top overdue unpaid bills before a cutoff date (ordered by most recent due_date)
export async function getOverdueBills(cutoffDate, bankAccountId = null, page = 1, limit = 20) {
  try {
    const params = new URLSearchParams();
    if (cutoffDate) params.append('due_date__lt', cutoffDate);
    // Only unpaid
    params.append('paid', 'false');
    // Most recent overdue first
    params.append('ordering', '-due_date');
    if (bankAccountId) params.append('bank_account', bankAccountId);
    params.append('page', page);
    params.append('limit', limit);

    const url = `/bills/?${params.toString()}`;
    const response = await axiosInstance.get(url);

    if (!response.data) {
      debugWarn('Empty response from overdue bills endpoint');
      return { bills: [], total: 0, hasMore: false, currentPage: page };
    }

    if (response.data.results && Array.isArray(response.data.results)) {
      const validBills = response.data.results.filter(bill => bill && bill.id && bill.name);
      return {
        bills: validBills,
        total: response.data.count || validBills.length,
        hasMore: !!response.data.next,
        currentPage: page,
      };
    }

    if (Array.isArray(response.data)) {
      const validBills = response.data.filter(bill => bill && bill.id && bill.name);
      return { bills: validBills, total: validBills.length, hasMore: false, currentPage: 1 };
    }

    debugWarn('Unexpected response from overdue bills endpoint');
    return { bills: [], total: 0, hasMore: false, currentPage: page };
  } catch (error) {
    debugError('Error fetching overdue bills:', error.message);
    debugError('Error fetching overdue bills (full details):', error);
    return { bills: [], total: 0, hasMore: false, currentPage: page };
  }
}

// Function to get bills for a specific bank account
export async function getBillsByBankAccount(bankAccountId, filterDate, isPaid, sortField, sortOrder) {
  return getBills(filterDate, isPaid, sortField, sortOrder, bankAccountId);
}

export async function getFutureMonthBills(page = 1, limit = 50) {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    
    const url = `/future-month-bills/?${params.toString()}`;
    const response = await axiosInstance.get(url);

    // Check if response data is null
    if (!response.data) {
      debugWarn('Empty response data from future-month-bills endpoint');
      return { bills: [], total: 0, hasMore: false };
    }
    
    // Handle paginated response format
    if (response.data.results && Array.isArray(response.data.results)) {
      const validBills = response.data.results.filter(bill => 
        bill && typeof bill === 'object' && bill.id && bill.name
      );
      
      return {
        bills: validBills,
        total: response.data.count || validBills.length,
        hasMore: !!response.data.next,
        currentPage: page
      };
    }
    
    // Handle legacy format (non-paginated)
    if (Array.isArray(response.data)) {
      const validBills = response.data.filter(bill => 
        bill && typeof bill === 'object' && bill.id && bill.name
      );
      
      return {
        bills: validBills,
        total: validBills.length,
        hasMore: false,
        currentPage: 1
      };
    }
    
    // Handle legacy format with results property
    if (response.data && typeof response.data === 'object' && Array.isArray(response.data.results)) {
      const validBills = response.data.results.filter(bill => 
        bill && typeof bill === 'object' && bill.id && bill.name
      );
      
      return {
        bills: validBills,
        total: response.data.count || validBills.length,
        hasMore: !!response.data.next,
        currentPage: page
      };
    }

    debugWarn('Unexpected response format from future-month-bills endpoint');
    return { bills: [], total: 0, hasMore: false };
  } catch (error) {
    debugError('Error fetching future bills:', error.message);
    debugError('Error fetching future bills (full details):', error);
    // Return empty array instead of throwing
    return { bills: [], total: 0, hasMore: false };
  }
}

export const updateBillNotes = async (billId, notes) => {
  // Input validation
  if (!billId || typeof billId !== 'number') {
    throw new Error('Invalid bill ID');
  }
  if (typeof notes !== 'string') {
    throw new Error('Invalid notes format');
  }

  try {
    // No need to re-sanitize here as we're already sanitizing in the modal component
    const response = await axiosInstance.patch(`/bills/${encodeURIComponent(billId)}/`, { notes });

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (typeof response.data.notes !== 'string') {
      throw new Error('Invalid notes response structure');
    }

    return response.data;
  } catch (error) {
    debugError('Error updating bill notes:', error.message);
    debugError('Error updating bill notes (full details):', error);
    throw error;
  }
};


export const deleteBill = async (billId, deleteSeries = false) => {
  // Input validation
  if (!billId || typeof billId !== 'number') {
    throw new Error('Invalid bill ID');
  }
  if (typeof deleteSeries !== 'boolean') {
    throw new Error('Invalid deleteSeries parameter');
  }

  try {
    const url = `/bills/${encodeURIComponent(billId)}/${deleteSeries ? '?delete_series=true' : ''}`;
    await axiosInstance.delete(url);
    return true;
  } catch (error) {
    debugError('Error deleting bill:', error.message);
    debugError('Error deleting bill (full details):', error);
    throw error;
  }
};


// Function to update the paid status of a bill
export async function updateBillPaidStatus(billId, paidStatus, paidDate, updatedBill) {
  // Input validation
  if (!billId || typeof billId !== 'number') {
    throw new Error('Invalid bill ID');
  }
  if (typeof paidStatus !== 'boolean') {
    throw new Error('Invalid paid status');
  }
  if (paidDate && isNaN(new Date(paidDate).getTime())) {
    throw new Error('Invalid paid date');
  }
  if (!updatedBill || typeof updatedBill !== 'object') {
    throw new Error('Invalid bill data');
  }

  const formattedDate = paidDate ? formatDate(paidDate) : null;
  const data = {
    name: updatedBill.name,
    description: updatedBill.description || null,
    amount: updatedBill.amount,
    frequency: updatedBill.frequency || 'by_date',
    due_day: updatedBill.due_day || null,
    due_date: updatedBill.due_date,
    paid: paidStatus,
    paid_date: formattedDate,
    num_bills: updatedBill.num_bills || 1,
    bill_series_id: updatedBill.bill_series_id,
    notes: updatedBill.notes || null,
    bank_account: updatedBill.bank_account
  };

  try {
    const response = await axiosInstance.put(`/bills/${encodeURIComponent(billId)}/`, data);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (typeof response.data.paid !== 'boolean') {
      throw new Error('Invalid paid status response');
    }

    return response.data;
  } catch (error) {
    debugError('Error updating bill paid status:', error.message);
    debugError('Error updating bill paid status (full details):', error);
    throw error;
  }
}

// Function to get total bills for this week
export const getTotalThisWeek = async (bankAccountId) => {
  try {
    const response = await axiosInstance.get(`/bills-this-week/`, {
      params: { bank_account: bankAccountId }
    });
    return response.data.total || "0.00";
  } catch (error) {
    debugError('Error fetching total for this week:', error.message);
    debugError('Error fetching total for this week (full details):', error);
    return "0.00";
  }
};

// Function to get total bills for next week
export const getTotalNextWeek = async (bankAccountId) => {
  try {
    const response = await axiosInstance.get(`/bills-next-week/`, {
      params: { bank_account: bankAccountId }
    });
    return response.data.total || "0.00";
  } catch (error) {
    debugError('Error fetching total for next week:', error.message);
    debugError('Error fetching total for next week (full details):', error);
    return "0.00";
  }
};

// Function to get total bills for the week after next
export const getTotalWeekAfterNext = async (bankAccountId) => {
  try {
    const response = await axiosInstance.get(`/bills-week-after-next/`, {
      params: { bank_account: bankAccountId }
    });
    return response.data.total || "0.00";
  } catch (error) {
    debugError('Error fetching total for week after next:', error.name);
    return "0.00";
  }
};

// Function to get total bills for the fortnight
export const getTotalFortnight = async (bankAccountId) => {
  try {
    const response = await axiosInstance.get(`/bills-fortnight/`, {
      params: { bank_account: bankAccountId }
    });
    
    return response.data.total || "0.00";
  } catch (error) {
    debugError('Error fetching fortnight\'s total:', error.name);
    return "0.00";
  }
};

// Function to get fortnight bills data (only totals - bills data comes from separate endpoint)
export const getFortnightBillsData = async (bankAccountId) => {
  try {
    const response = await axiosInstance.get(`/bills-fortnight/`, {
      params: { bank_account: bankAccountId }
    });
    
    return {
      total: response.data.total || 0,
      this_week: response.data.this_week || 0,
      next_week: response.data.next_week || 0
    };
  } catch (error) {
    debugError('Error fetching fortnight bills data:', error.name);
    return {
      total: 0,
      this_week: 0,
      next_week: 0
    };
  }
};

// Function to get bills for fortnight date range (following the pays pattern)
export const getBillsForFortnight = async (bankAccountId = null, startDate, endDate) => {
  try {
    const params = {
      start_date: startDate,
      end_date: endDate
    };
    
    if (bankAccountId) {
      params.bank_account = bankAccountId;
    }
    
    debugLog('Getting bills for fortnight:', params);
    
    const response = await axiosInstance.get('/bills/', { params });
    
    return {
      bills: response.data.results || [],
      count: response.data.count || 0
    };
  } catch (error) {
    debugError('Error fetching bills for fortnight:', error.message);
    debugError('Error fetching bills for fortnight (full details):', error);
    return {
      bills: [],
      count: 0
    };
  }
};

// General function to fetch total amounts
const fetchTotal = async (url) => {
  try {
    const response = await axiosInstance.get(url);
    return response.data.totalAmount;
  } catch (error) {
    debugError('Error fetching total:', error.name);
    return 0;
  }
};

// Function to filter bills by the current week
export function filterBillsByCurrentWeek(bills, lastThursday, nextThursday) {
  return bills
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .filter((bill) => {
      const dueDate = new Date(bill.due_date);
      const paidDate = bill.paid ? new Date(bill.paid_date) : null;
      const adjustedNextThursday = new Date(nextThursday);
      adjustedNextThursday.setHours(0, 0, 0, 0);

      const isDueWithinPeriod = dueDate >= lastThursday && dueDate < adjustedNextThursday;
      const wasPaidWithinPeriod = bill.paid && paidDate >= lastThursday && dueDate < adjustedNextThursday;
      const isDueBeforeNextThursday = !bill.paid && dueDate < adjustedNextThursday;

      return isDueWithinPeriod || wasPaidWithinPeriod || isDueBeforeNextThursday;
    });
}

// Function to filter bills by next week
export function filterBillsByNextWeek(bills, nextThursday, fortnightThurs) {
  return bills.filter(bill => {
    const dueDate = new Date(bill.due_date);
    return dueDate >= nextThursday && dueDate < fortnightThurs;
  });
}

// Function to get all bills without any filtering
export async function getAllBills() {
  try {
    const response = await axiosInstance.get('/bills/');
    
    // Check if response data is valid
    if (!response.data) {
      debugWarn('Empty response data from bills endpoint');
      return [];
    }
    
    // Handle non-array responses
    if (!Array.isArray(response.data)) {
      debugWarn('Bills response is not an array');
      return [];
    }

    // Filter out obviously invalid items
    const validBills = response.data.filter(bill => 
      bill && typeof bill === 'object' && bill.id && bill.name
    );

    return validBills;
  } catch (error) {
    debugError('Error fetching all bills:', error.name);
    // Return empty array instead of throwing error to avoid UI crashes
    return [];
  }
}
